import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AddonPacksService from '../../../Api/AddonPacksService';
import { toast } from 'react-toastify';

const AddAddonPacks = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const packId = new URLSearchParams(location.search).get('id');
    const PackAPI = new AddonPacksService();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        pack_name: '',
        description: '',
        price: '',
        points_provided: '',
        is_active: true,
        thumbnail_url: 'https://www.contentviewspro.com/wp-content/uploads/2017/07/default_image.png'
    });

    useEffect(() => {
        const fetchPack = async () => {
            if (packId) {
                try {
                    setIsLoading(true);
                    const response = await PackAPI.GetById(packId);
                    if (response.data && response.data.data) {
                        const packData = response.data.data;
                        setFormData({
                            pack_name: packData.pack_name || '',
                            description: packData.description || '',
                            price: packData.price || '',
                            points_provided: packData.points_provided || '',
                            is_active: packData.is_active,
                            thumbnail_url: packData.thumbnail_url || 'https://www.contentviewspro.com/wp-content/uploads/2017/07/default_image.png'
                        });
                    }
                } catch (err) {
                    setError('Failed to load addon pack data. Please try again.');
                    console.error('Error loading addon pack:', err);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchPack();
    }, [packId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            let response;
            if (packId) {
                // Update existing pack
                response = await PackAPI.UpdateByID(formData, packId);
            } else {
                // Create new pack
                response = await PackAPI.Addaddonpacks(formData);
            }

            if (response.data.status === "success") {
                toast.success(packId ? 'Addon pack updated successfully!' : 'Addon pack created successfully!');
                navigate('/addon-packs-list');
            } else {
                throw new Error(response.data.message || 'Failed to save addon pack');
            }
        } catch (error) {
            setError(error.response?.data?.message || error.message || 'Failed to save addon pack');
            toast.error(error.response?.data?.message || 'An error occurred while saving');
            console.error('Addon pack save error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body">
                <h4 className="text-xl text-primary-light mb-24">
                    {packId ? 'Edit Addon Pack' : 'Add New Addon Pack'}
                </h4>
                {error && (
                    <div className="alert alert-danger mb-3" role="alert">
                        {error}
                    </div>
                )}
                {isLoading && !formData.pack_name ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="row g-4">
                            <div className="col-md-4">
                                {formData.thumbnail_url && (
                                    <div className="mt-3">
                                        <img 
                                            src={formData.thumbnail_url} 
                                            alt="Pack thumbnail" 
                                            className="img-fluid rounded"
                                            style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                                        />
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">
                                        Thumbnail URL <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="url"
                                        className="form-control"
                                        name="thumbnail_url"
                                        value={formData.thumbnail_url}
                                        onChange={handleChange}
                                        placeholder="Enter thumbnail URL"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Price <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="price"
                                        value={formData.price}
                                        onChange={handleChange}
                                        placeholder="0"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Points Provided <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="points_provided"
                                        value={formData.points_provided}
                                        onChange={handleChange}
                                        placeholder="0"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleChange}
                                            id="isActive"
                                        />
                                        <label className="form-check-label" htmlFor="isActive">
                                            Active
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-8">
                                <div className="mb-3">
                                    <label className="form-label">
                                        Pack Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="pack_name"
                                        value={formData.pack_name}
                                        onChange={handleChange}
                                        placeholder="Enter pack name"
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">
                                        Description <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        placeholder="Enter pack description"
                                        rows="4"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-center gap-3 mt-32">
                            <button
                                type="button"
                                className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        {packId ? 'Updating...' : 'Saving...'}
                                    </>
                                ) : (
                                    packId ? 'Update Pack' : 'Save Pack'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default AddAddonPacks;
